import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { ReactComponent as TextHighlightIcon } from '@assets/icons/ShapesDrawingTools/text_highlight.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as CheckSvg } from '@assets/icons/check.svg';
import { MdWidth } from '@components/Responsive';
import { useSelectedMVComment, useAssetMVPermissions } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import ControlBar from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/ControlBar';
import ShapesDrawingTools from '@pages/MediaViewer/AssetViewer/ShapesDrawingTools';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import { commentHasFloatAnchor } from '@pages/MediaViewer/components/FloatCommentItem/floatCommentHelpers';
import { AssetItemDto, AssetVersionItemDto } from '@api/Api';
import './MediaViewerMobileAction.less';

export type MediaViewerMobileActionVisibility = {
  showSidebar: boolean;
  showShapesToolbar: boolean;
  showShapesCommentsToggle: boolean;
  showPanelAction: boolean;
  showControlBar: boolean;
  showFloatCommentEditor: boolean;
  showFloatCommentItem: boolean;
};

export function useMediaViewerMobileActionVisibility({
  isShowFloatCommentForm,
  isShowFloatCommentItem
}: {
  isShowFloatCommentForm: boolean;
  isShowFloatCommentItem: boolean;
}): MediaViewerMobileActionVisibility | null {
  const { isMdWidth } = useResponsive();
  const {
    selectedCommentId,
    selectedVersion,
    shapesDrawingTools,
    isCommentFieldFocused,
    isHideComments,
    activePageNumber
  } = useAssetViewerState();
  const selectedComment = useSelectedMVComment();

  return useMemo(() => {
    if (!isMdWidth) return null;

    const assetType = selectedVersion?.metadata.type;
    const isDocument =
      assetType === 'document' ||
      assetType === 'presentation' ||
      assetType === 'spreadsheet';

    const isFloatCommentEditorActive =
      isShowFloatCommentForm &&
      selectedCommentId === 'new' &&
      shapesDrawingTools.shape !== 'cursor';

    const isFloatCommentEditorToolsActive =
      isFloatCommentEditorActive && isCommentFieldFocused;

    const isCommentOnActivePage =
      !isDocument ||
      selectedComment?.documentPage == null ||
      selectedComment.documentPage === activePageNumber;

    const isReplyMode = selectedCommentId === 'reply';

    const isFloatCommentItemActive =
      isShowFloatCommentItem &&
      !isHideComments &&
      !!selectedCommentId &&
      selectedCommentId !== 'new' &&
      (isReplyMode ||
        (!!selectedComment &&
          commentHasFloatAnchor(selectedComment) &&
          isCommentOnActivePage));

    return {
      showFloatCommentEditor: isFloatCommentEditorActive,
      showFloatCommentItem: isFloatCommentItemActive,
      showSidebar: !isFloatCommentItemActive && !isFloatCommentEditorActive,
      showShapesCommentsToggle:
        !isFloatCommentItemActive && !isFloatCommentEditorActive,
      showShapesToolbar:
        !isFloatCommentItemActive && !isFloatCommentEditorToolsActive,
      showPanelAction: !isFloatCommentItemActive,
      showControlBar:
        !isFloatCommentItemActive && !isFloatCommentEditorToolsActive
    };
  }, [
    isMdWidth,
    isShowFloatCommentForm,
    isShowFloatCommentItem,
    selectedCommentId,
    shapesDrawingTools.shape,
    isCommentFieldFocused,
    isHideComments,
    activePageNumber,
    selectedComment,
    selectedVersion?.metadata.type
  ]);
}

type MediaViewerMobileActionProps = {
  visibility: MediaViewerMobileActionVisibility;
  asset: AssetItemDto;
  selectedVersion: AssetVersionItemDto;
  commentMentions: boolean;
  canCreateTask: boolean;
  isShowFloatCommentForm: boolean;
  isShowFloatCommentItem: boolean;
  isContainerExpand?: boolean;
  isShowShapeDrawingTools?: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
};

function MediaViewerMobileAction({
  visibility,
  asset,
  selectedVersion,
  commentMentions,
  canCreateTask,
  isShowFloatCommentForm,
  isShowFloatCommentItem,
  isContainerExpand = false,
  isShowShapeDrawingTools = true,
  setSelectedCollapse
}: MediaViewerMobileActionProps) {
  const canLeaveComment = useAssetMVPermissions('comment');
  const { isMdWidth } = useResponsive();
  const {
    getPlaybackPlayer,
    shapesDrawingTools,
    documentHighlight,
    setDocumentHighlight,
    setDrawToolsShape
  } = useAssetViewerState();
  const player = getPlaybackPlayer();

  const [isShowVideo, setIsShowVideo] = useState<boolean>(false);

  const assetType = selectedVersion?.metadata.type;
  const isVideo = assetType === 'video';
  const isAudio = assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';

  const shapesClassName = classNames(
    isVideo && 'b-media_viewer_video',
    isAudio && 'b-media_viewer_audio',
    isDocument && 'b-media_viewer_document',
    isContainerExpand && 'b-media_viewer_asset-expanded'
  );
  const isTextHighlightShape = shapesDrawingTools.shape === 'documentHighlight';
  const selectedCharsCount = documentHighlight?.text?.length || 0;
  const isShowTextHighlightMobile =
    isDocument && isMdWidth && isTextHighlightShape;

  const showShapesDrawingTools =
    canLeaveComment &&
    isShowShapeDrawingTools &&
    (visibility.showShapesToolbar || visibility.showShapesCommentsToggle);

  useEffect(() => {
    return () => {
      setIsShowVideo(false);
    };
  }, [visibility.showFloatCommentItem]);

  useEffect(() => {
    if (!isMdWidth) return;
    if (!visibility.showSidebar) return;
    document.getSelection()?.removeAllRanges();
    setDocumentHighlight(null);
    setDrawToolsShape('cursor');
  }, [
    isMdWidth,
    visibility.showSidebar,
    setDocumentHighlight,
    setDrawToolsShape
  ]);

  const onCancelTextHighlight = () => {
    document.getSelection()?.removeAllRanges();
    setDocumentHighlight(null);
    setDrawToolsShape('cursor');
  };

  const onConfirmTextHighlight = () => {
    setDrawToolsShape('cursor');
  };

  const controlBar = useMemo(
    () =>
      player && (isVideo || isAudio)
        ? createPortal(
            <ControlBar
              player={player}
              asset={selectedVersion}
              setSelectedCollapse={setSelectedCollapse}
              className={classNames(
                isShowVideo && 'video_control_bar_mobile-show-video'
              )}
            />,
            player.el()
          )
        : null,
    [
      player,
      isVideo,
      isAudio,
      selectedVersion,
      setSelectedCollapse,
      isShowVideo
    ]
  );

  return (
    <MdWidth>
      <div
        className={classNames('media_viewer_mobile_action', {
          'media_viewer_mobile_action-show-video': isShowVideo
        })}
      >
        {isShowTextHighlightMobile && (
          <div className="text-highlight-mobile-container">
            <div className="text-highlight-mobile-icon">
              <TextHighlightIcon />
            </div>
            <div className="text-highlight-mobile-content">
              {selectedCharsCount
                ? `${selectedCharsCount} characters selected`
                : 'Select text to attach'}
            </div>
            <div className="text-highlight-mobile-btn-container">
              <div
                className="text-highlight-mobile-btn"
                onClick={onCancelTextHighlight}
              >
                <CrossSvg width={20} height={20} />
              </div>
              {!!selectedCharsCount && (
                <div
                  className="text-highlight-mobile-btn"
                  onClick={onConfirmTextHighlight}
                >
                  <CheckSvg width={14} height={14} />
                </div>
              )}
            </div>
          </div>
        )}

        {showShapesDrawingTools && !isShowTextHighlightMobile && (
          <ShapesDrawingTools
            player={getPlaybackPlayer()}
            className={shapesClassName}
          />
        )}
        {isShowFloatCommentForm &&
          visibility.showFloatCommentEditor &&
          !isShowTextHighlightMobile && (
            <FloatCommentEditor
              asset={asset}
              canCreateTask={canCreateTask}
              commentMentions={commentMentions}
              selectedVersion={selectedVersion}
            />
          )}
        {isShowFloatCommentItem &&
          visibility.showFloatCommentItem &&
          !isShowTextHighlightMobile && (
            <FloatCommentItem
              asset={asset}
              selectedVersion={selectedVersion}
              commentMentions={commentMentions}
              canCreateTask={canCreateTask}
              isShowVideo={isShowVideo}
              onExpandVideo={(value) => setIsShowVideo(value)}
            />
          )}
        {(visibility.showControlBar || isShowVideo) && controlBar}
      </div>
    </MdWidth>
  );
}

export default MediaViewerMobileAction;
